import { ClipboardCheck, Copy, SaveIcon, SquarePen, X } from "lucide-react";
import { Popover } from "radix-ui";
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import ButtonV2 from "../../../components/ui/button/v2";
import { cn } from "../../../lib/utils";
import { cloneElement } from "react";

import { EditableInfoField } from "../../../../src/components/ui/custominfo/EditableInfoField";
import SelectDropdownV2 from "../../../../src/components/ui/selectdropdown/v2"; 
import { OptionNameValuePair } from "../../../../src/lib/interfaces/types";
import { useAppDispatch, useAppSelector } from "../../../../src/app/store";
import { cancelEditMode, selectCaseHeaderIsUpdating, selectEditableField, setEditableField, setEditMode } from "../logic/Slice";
import { selectParticipantsIsUpdating } from "../logic/ParticipantsSlice";

export interface OverviewItemProps {
    icon: React.ReactNode;
    label: string;
    value?: string;
    displayValue?: string; 
    isVertical?: boolean;
    isEditable?: boolean;
    onSave?: (value: any) => Promise<boolean>;
    type?: "text" | "number" | "select";
    inputError?: string | null;
    popoverContent?: React.ReactNode;
    inputPlaceholder?:string;
    selectOptions?: OptionNameValuePair<any>[];
    field?: string;
}

export function OverviewItem({
    icon, label, isVertical = false, 
    isEditable = false, 
    onSave,
    type,
    value, 
    displayValue, 
    inputError,
    popoverContent,
    inputPlaceholder,
    selectOptions, 
    field
}: OverviewItemProps) {
    const internalPopoverRef = useRef<any>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const dispatch = useAppDispatch();
    const editableField = useAppSelector(selectEditableField);
    const isCaseUpdating = useAppSelector(selectCaseHeaderIsUpdating);
    const isParticipantUpdating = useAppSelector(selectParticipantsIsUpdating);
    const isEditing = field ? editableField === field : false;
    const isSaving = isEditing && (isCaseUpdating || isParticipantUpdating);
    const isAnyUpdating = isCaseUpdating || isParticipantUpdating;

    const [localValue, setLocalValue] = useState<string | undefined>(value);

    const handleEditClick = () => {
        if (isAnyUpdating) return;
        if (isSaving) return;
        if (!field) return;

        dispatch(setEditMode(true));
        dispatch(setEditableField(field));
    };

    const handleCancel = () => {
        setLocalValue(value);
        dispatch(cancelEditMode());
    };

    useEffect(() => {
        if (isEditing && type !== "select") {
            inputRef.current?.focus();
        }
    }, [isEditing]);

    useEffect(() => {
        if (editableField !== field) {
            setLocalValue(value);
        }
    }, [editableField]);

    const onChange = (value: string) => {
        setLocalValue(value);
    }

    const handleSave = async () => {
        if(localValue === value){
            dispatch(cancelEditMode())
            return;
        }

        if(!onSave) {
            return
        }

        const isSuccess = await onSave(localValue);

        if(isSuccess) {
            dispatch(dispatch(cancelEditMode()));
        }
    }

    const renderContent = () => {
        if (type === "select" && isEditing) {
            const selectedOption = localValue && localValue !== "" ? selectOptions?.find(opt => opt.value === localValue) : undefined;

            return (
                <div className="flex flex-col">
                    <div className="max-w-[220px]">
                        <SelectDropdownV2
                            selectedOption={selectedOption}
                            name={label}
                            placeholder="Search"
                            options={selectOptions || []}
                            onSelectionChange={(val) => onChange?.(val.value)}
                            dropdownClassName="!w-full max-w-[220px]"
                            optionClassName="!whitespace-normal break-words [&_span]:!whitespace-normal"
                            disabled={isSaving}
                        />
                    </div>
                    {inputError && <p className="mt-1 text-xs text-red-600">{inputError}</p>}

                </div>
            );
        }

        if ((type === "text" || type === "number") && isEditing) {
            return (
                <div className="min-w-0">
                    <EditableInfoField
                        ref={inputRef}
                        label=""
                        name={label.toLowerCase().replace(/\s+/g, "_")}
                        placeholder={inputPlaceholder}  
                        value={String(localValue ?? '')}
                        isEditing={isEditing && !isSaving}
                        onChange={(e) => onChange?.(e.target.value)}
                        orientation="horizontal"
                        containerClassName="py-1"
                        type={type}
                        valueClassName={isEditing && !isSaving ? `w-full bg-[#F3F4F6]  ml-0 border-0 py-0 shadow-none pl-1 rounded-[8px] font-normal ${isVertical? "p-1": ""}` : "font-normal"}
                    />
                    {inputError && <p className="mt-1 text-xs text-red-600">{inputError}</p>}
                </div>
            );
        }

      
        const finalValueText = displayValue ? displayValue : localValue;

        if (popoverContent) {
            return (
                <div 
                    className="grow w-full h-full flex min-w-0" 
                    onMouseEnter={() => internalPopoverRef.current?.open()} 
                    onMouseLeave={() => internalPopoverRef.current?.close()}
                >
                    <div className="min-w-0 overflow-hidden truncate text-left text-sm font-medium text-[#475569]">
                        {finalValueText}
                    </div>
                    {popoverContent &&
                        cloneElement(
                            popoverContent as React.ReactElement<any>,
                            { ref: internalPopoverRef }
                        )
                    }
                    {inputError && <p className="mt-1 text-xs text-red-600">{inputError}</p>}
                </div>
            );
        }

        if (typeof finalValueText === "string" && finalValueText.length > 0) {
            return (
                <div className={cn("text-sm text-[#475569] font-medium","truncate")} title={finalValueText}>
                    {finalValueText}
                </div>
            );
        }

        if (inputPlaceholder) {
            return (
                <div
                className="text-sm font-normal truncate bg-[#F3F4F6] p-2 rounded-[8px] text-[#94A3B8]"
                >
                {inputPlaceholder}
                </div>
            );
        }
        
        return null;
    };

    return (
        <div className={cn("grid gap-3 group", isVertical ? "gap-1 grid-cols-[auto_1fr_auto] grid-rows-[auto_auto]" : "grid-cols-[auto_minmax(0,1fr)_auto] items-center")}>
            <div className={cn("flex items-center gap-3 min-w-0", isVertical && "col-span-2 row-start-1")}>
                <div className="w-4 rounded-md flex items-center justify-center text-gray-500 shrink-0">
                    {icon}
                </div>
                <div className="text-xs font-medium shrink-0" style={{ color: "#94A3B8", width: 180 }}>
                    {label}
                </div>
            </div>

            <div className={cn("min-w-0 flex-1", isVertical && "col-start-1 col-span-2 row-start-2")}>
                {renderContent()}
            </div>

            {isEditable ? (
                <div className={cn("flex items-center gap-1 justify-end shrink-0", isVertical && "col-start-3 row-start-2")}>
                    {isSaving ? (
                        <span className="text-xs text-gray-500 italic">Saving...</span>
                    ) : !isEditing? (
                            <ButtonV2 variant="ghost" className="p-0 w-[28px] h-[28px] bg-slate-200 hover:bg-slate-100 active:bg-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" onClick={handleEditClick} prefix={<SquarePen color="#737373" fill="none" width={16} />} />
                    ) : (
                        <>
                            <ButtonV2 variant="ghost" className="p-0 w-[28px] h-[28px] bg-slate-200 hover:bg-slate-100 active:bg-slate-300" onClick={handleSave} prefix={<SaveIcon color="#737373" fill="none" width={16} />} />
                            <ButtonV2 variant="ghost" className="p-0 w-[28px] h-[28px] active:bg-slate-300" onClick={handleCancel} prefix={<X color="#737373" fill="none" width={16} />} />
                        </>
                    )}
                </div>
            ) : <div />}
        </div>
    );
}

export function OverviewFooterItem({
    icon,
    label,
    value,
    valueNode,
    wrap = false,
    className = '',
    contentClassName = ''
}: {
    icon: React.ReactNode;
    label: string;
    value?: string;
    valueNode?: React.ReactNode;
    wrap?: boolean;
    className?: string;
    contentClassName?: string;
}) {
    return (
        <div className={cn("flex flex-col gap-2 min-w-0", className)}>
            <div className="flex gap-1 items-center shrink-0">
                <div className="text-gray-400 shrink-0">
                    {icon}
                </div>
                <div className="text-xs font-medium text-gray-400 shrink-0" style={{ color: "#94A3B8" }}>{label}</div>
            </div>
            <div className={cn("min-w-0 w-full", contentClassName)}>
                {!!value ?
                    <div
                        className={`text-sm font-medium text-gray-900 ${wrap ? "whitespace-normal" : "truncate"}`}
                        style={{ color: "#475569" }}
                        title={value}
                    >
                        {value}
                    </div>
                    :
                    valueNode ? valueNode : null
                }
            </div>
        </div>
    );
}

export const CaseListPopover = forwardRef((props: {
    id: string;
    icon?: React.ReactNode;
    title?: string;
    allowCopy?: boolean;
    variant?: "default" | "twoColumn";
    items: any[];
}, ref) => {
    const [open, setOpen] = useState(false);
    const [copiedIcon, setCopiedIcon] = useState<number | null>(null);

    useImperativeHandle(ref, () => ({
        open: () => setOpen(true),
        close: () => setOpen(false),
        toggle: () => setOpen(v => !v)
    }), []);

    return (
        <Popover.Root key={props.id} open={open} onOpenChange={setOpen}>
            <Popover.Anchor />
            <Popover.Content
                className="z-[50]"
                align={(props.variant ?? "default") === "twoColumn" ? "end" : "center"}
            >
                <div
                    className={cn(
                        "py-2 px-3 bg-white rounded-md shadow-overviewCard border border-gray-200",
                        (props.variant ?? "default") === "twoColumn"
                            ? "min-w-[260px] w-fit max-w-[min(560px,90vw)]"
                            : "min-w-[200px]"
                    )}
                >
                    {(props.title || props.icon) &&
                        <div className="flex gap-2 items-center mb-2">
                            {props.icon && <div className="text-slate-400">{props.icon}</div>}
                            {props.title && <div className="text-xs font-semibold text-slate-400">{props.title}</div>}
                        </div>
                    }
                    <div className="flex flex-col">
                        {(props.variant ?? "default") === "twoColumn"
                            ? props.items.map((rawItem, index) => {
                                const item = rawItem as {
                                    label?: React.ReactNode;
                                    value?: React.ReactNode;
                                    copyText?: string;
                                };
                                const copyText = item?.copyText ?? (typeof item?.value === "string" ? item.value : "");

                                return (
                                    <div
                                        className="grid grid-cols-[minmax(160px,260px)_minmax(0,1fr)_auto] items-start gap-x-4 gap-y-1 group"
                                        key={index}
                                    >
                                        <div
                                            className="py-1 min-w-0 font-sans font-normal text-sm leading-5 text-[#737373] whitespace-normal break-words"
                                            title={typeof item?.label === "string" ? item.label : undefined}
                                        >
                                            {item?.label ?? ""}
                                        </div>
                                        <div
                                            className="py-1 min-w-0 text-sm leading-5 text-[#475569] whitespace-normal break-words"
                                            title={typeof item?.value === "string" ? item.value : undefined}
                                            style={{ overflowWrap: "anywhere" }}
                                        >
                                            {item?.value ?? ""}
                                        </div>
                                        {props.allowCopy && (
                                            <button
                                                className="w-6 h-6 p-0.5 inline-flex items-center justify-center rounded hover:bg-gray-100 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                                                onClick={() => {
                                                    navigator.clipboard.writeText(String(copyText));
                                                    setCopiedIcon(index);
                                                    setTimeout(() => setCopiedIcon(null), 1000);
                                                }}
                                                aria-label="Copy"
                                            >
                                                {copiedIcon === index ? (
                                                    <ClipboardCheck className="h-3.5 w-3.5 text-green-400" />
                                                ) : (
                                                    <Copy className="h-3.5 w-3.5 text-gray-400" />
                                                )}
                                            </button>
                                        )}
                                    </div>
                                );
                            })
                            : props.items.map((item, index) => (
                                <div
                                    className="flex items-center justify-between group"
                                    key={index}
                                >
                                    <div className="py-1 text-sm truncate">{item}</div>
                                    {props.allowCopy && (
                                        <button
                                            className="ml-2 p-1 rounded hover:bg-gray-100 opacity-0 group-hover:opacity-100 transition-opacity"
                                            onClick={() => {
                                                navigator.clipboard.writeText(String(item));
                                                setCopiedIcon(index);
                                                setTimeout(() => setCopiedIcon(null), 1000);
                                            }}
                                            aria-label="Copy"
                                        >
                                            {copiedIcon === index ? (
                                                <ClipboardCheck className="h-4 w-4 text-green-400" />
                                            ) : (
                                                <Copy className="h-4 w-4 text-gray-400" />
                                            )}
                                        </button>
                                    )}
                                </div>
                            ))}
                    </div>
                </div>
            </Popover.Content>
        </Popover.Root>
    );
});